import React, { useCallback, useState } from 'react';
import {
  Platform,
  StyleSheet,
  ImageStyle,
  ImageSourcePropType,
} from 'react-native';

import Colors from '../../constants/Colors';
import { useAuthenticate } from '../../contexts/authFlow';
import useColorScheme from '../../hooks/useColorScheme';
import { useMaxWidth, useMinWidth } from '../../hooks/useResponsive';
import BumoCarousel from '../BumoCarousel';
import { PrimaryButton } from '../Button';
import { Icon } from '../Icon';
import { Image } from '../Image';
import { InnerContainer } from '../Layout';
import { TransparentRow } from '../Row';
import { TextBody, TextH2, TextTag } from '../StyledText';
import { StyleProps, TransparentView, View } from '../Themed';

const reviewHeight = 530;

const reviewsDataV2: {
  text: string;
  author: string;
  avatar: ImageSourcePropType;
}[] = [
  {
    text: `Bumo was amazing in coordinating care for us during a very stressful time. We found some wonderful care providers that we would not hesitate to use again.`,
    author: 'monica c.',
    avatar: require('../../assets/images/testimonial-1.png'),
  },
  {
    text: `I love bumo! I love the variety of camps offered, tailored to different needs and preferences. Also love the flexibility and ease of booking. The staff is very helpful as well 💕`,
    author: 'Yauheniya B.',
    avatar: require('../../assets/images/testimonial-2.png'),
  },
  {
    text: `My schedule can be unpredictable at times so I love that there are many locations and times for me to drop my daughters off for last minute child care! One time I booked Bumo 30 minutes before the start time - it's that easy! I can honestly say I would recommend Bumo to every mom that needs child care.`,
    author: 'nina S., mama to aivy & haly',
    avatar: require('../../assets/images/testimonial-3.png'),
  },
  {
    text: 'My son had the best time at the art camp he attended through Bumo. We were scrambling for what to do for child care and my friend recommended Bumo. I’m so thankful because the whole process was easy and the people I spoke with were all so friendly and helpful. I can’t wait to sign him up for another day. Thank you!',
    author: 'Suzanne S.',
    avatar: require('../../assets/images/testimonial-4.png'),
  },
  {
    text: 'Booking on-demand child care through Bumo was a lifesaver for my family, especially when our nanny unexpectedly quit. Thanks to their easy-to-use platform, I was able to quickly find reliable care for my children. Bumo truly made the process stress-free and convenient!',
    author: 'Whitney k., mama to bear & georgie',
    avatar: require('../../assets/images/testimonial-5.png'),
  },
  {
    text: `I really like using Bumo whenever I need last-minute and drop-in care. The customer service is also helpful and responsive. I love what the company represents, trying to help parents and their children make child care work when they need it the most. Overall, I have had a good experience with Bumo. Highly recommend!`,
    author: 'roseanna l.',
    avatar: require('../../assets/images/testimonial-6.png'),
  },
  {
    text: `Where do I even start raving about Bumo? They're like the extended family I never knew I needed. Bumo is the Mary Poppins bag of child care magic. If you’re a busy parent, these guys are it.`,
    author: `onelia m., mama to isla`,
    avatar: require('../../assets/images/testimonial-7.png'),
  },
  {
    text: `I have been using Bumo for over a year and have had the best experience. I recommend Bumo to all parents. Choosing child care is so hard but you’re in good hands with Bumo!`,
    author: `kristin k., mama to clea`,
    avatar: require('../../assets/images/testimonial-8.png'),
  },
  {
    text: `Bumo has truly been such a gift for our family! It’s not easy to find quality and trustworthy child care or camps, but with Bumo you can be rest assured that your little ones are in great hands. We are so thankful to have found Bumo and highly recommend them to all parents looking for safe and trustworthy child care or camps!`,
    author: `alyssa s., mama to kai & noa`,
    avatar: require('../../assets/images/testimonial-9.png'),
  },
  {
    text: `Bumo has allowed our family to find a system that works for our family/work dynamic. Bumo has created a trusting, creative, and loving environment that parents could only hope for.`,
    author: `kailey D.`,
    avatar: require('../../assets/images/testimonial-10.png'),
  },
];

export default function Testimonials({
  endAdornment,
  style,
}: {
  endAdornment?: React.ReactNode;
  style?: StyleProps['style'];
}) {
  const isLargeDevice = useMinWidth(768);
  const theme = useColorScheme();
  const authenticate = useAuthenticate();

  const data = reviewsDataV2;
  return (
    <View style={[{ backgroundColor: Colors[useColorScheme()].accent }, style]}>
      <InnerContainer style={styles.container}>
        {Platform.OS === 'web' && isLargeDevice && (
          <Image
            style={styles.spring as ImageStyle}
            source={require('../../assets/images/spring.png')}
          />
        )}
        {Platform.OS === 'web' && isLargeDevice && (
          <Image
            style={[
              styles.ribbon as ImageStyle,
              { tintColor: Colors[theme].accentSecondary },
            ]}
            source={require('../../assets/images/ribbon.png')}
          />
        )}
        <TransparentView style={[styles.reviewsOuter, { paddingBottom: 10 }]}>
          <TextH2 style={{ alignSelf: 'center' }}>Parents love Bumo!</TextH2>
        </TransparentView>
        <ResponsiveCarouselContainer
          forcedHeight={!isLargeDevice ? 'auto' : reviewHeight + 50}
        >
          {data.map((r, index) => (
            <ReviewBlock
              {...r}
              key={`review-${index}`}
              forcedHeight={!isLargeDevice ? 'auto' : reviewHeight}
            />
          ))}
        </ResponsiveCarouselContainer>
        <TransparentView style={styles.reviewsOuter}>
          {endAdornment ? (
            endAdornment
          ) : (
            <PrimaryButton
              title="Start Free Account"
              style={{ alignSelf: 'center' }}
              onPress={() => authenticate()}
            />
          )}
        </TransparentView>
      </InnerContainer>
    </View>
  );
}

function ResponsiveCarouselContainer({
  children,
  forcedHeight,
}: {
  children: React.ReactNode;
  forcedHeight?: number | 'auto';
}) {
  const isSmallDevice = useMaxWidth(850);
  const isLargeDevice = useMinWidth(1280);

  const [index, setIndex] = useState(0);
  const onIndexChange = useCallback((index: number) => {
    setIndex(index);
  }, []);

  return (
    <>
      <TransparentView
        style={[
          {
            position: 'relative',
            height: forcedHeight || 450,
            justifyContent: 'center',
          },
          !isSmallDevice && {
            paddingHorizontal: 15,
          },
        ]}
      >
        <BumoCarousel
          renderedElements={isLargeDevice ? 3 : isSmallDevice ? 1 : 2}
          onIndexChange={onIndexChange}
          itemContainerStyle={{ paddingHorizontal: 15 }}
          showArrows={!isSmallDevice}
          containerStyle={!isSmallDevice && { paddingHorizontal: 30 }}
        >
          {children}
        </BumoCarousel>
      </TransparentView>

      {isSmallDevice ? (
        <Dots activeIndex={index} style={{ marginTop: 10 }} />
      ) : null}
    </>
  );
}

function Dots({
  activeIndex = 0,
  style,
}: {
  activeIndex: number;
  style?: StyleProps['style'];
}) {
  const theme = useColorScheme();
  return (
    <TransparentRow style={style}>
      {reviewsDataV2.map((r, idx) => (
        <View
          style={[
            styles.dot,
            { backgroundColor: Colors[theme].background },
            idx === activeIndex && {
              backgroundColor: Colors[theme].accentTertiary,
            },
          ]}
          key={`dot-${idx}`}
        />
      ))}
    </TransparentRow>
  );
}

function ReviewBlock({
  text,
  author,
  avatar,
  forcedHeight,
}: {
  text: string | undefined;
  author: string;
  avatar: ImageSourcePropType;
  forcedHeight?: number | 'auto';
}) {
  const theme = useColorScheme();

  return (
    <View
      style={[
        styles.reviewBlock,
        { height: forcedHeight || styles.reviewBlock.height },
      ]}
    >
      <Image source={avatar} style={styles.reviewAvatar as ImageStyle} />
      <TransparentRow>
        <Icon name="star" style={{ tintColor: Colors[theme].cta }} />
        <Icon name="star" style={{ tintColor: Colors[theme].cta }} />
        <Icon name="star" style={{ tintColor: Colors[theme].cta }} />
        <Icon name="star" style={{ tintColor: Colors[theme].cta }} />
        <Icon name="star" style={{ tintColor: Colors[theme].cta }} />
      </TransparentRow>
      <View style={{ padding: 10 }}>
        <TextBody style={{ textAlign: 'center' }}>
          &ldquo;{text}&rdquo;
        </TextBody>
      </View>

      <View style={{ alignSelf: 'center', paddingTop: 10 }}>
        <TextTag>{author}</TextTag>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 0,
  },
  reviewsOuter: {
    padding: 30,
  },
  reviewsOuterDesktop: {
    paddingVertical: 45,
    paddingHorizontal: 120,
  },
  reviewsDesktop: {
    backgroundColor: 'transparent',
    flexDirection: 'column',
    paddingVertical: 30,
    ...Platform.select({
      web: {
        gap: 30,
      },
    }),
  },
  carouselItem: {
    paddingHorizontal: 15,
    borderWidth: 0,
    flex: 1,
    justifyContent: 'center',
  },
  reviewBlock: {
    display: 'flex',
    height: 400,
    paddingHorizontal: 35,
    paddingVertical: 20,
    borderRadius: 10,
    ...Platform.select({
      web: {
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)',
      },
    }),
  },
  reviewAvatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignSelf: 'center',
    marginBottom: 20,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: 'red',
    marginHorizontal: 3,
  },
  spring: {
    position: 'absolute',
    width: 76,
    height: 221,
    right: '1%',
    top: 0,
    transform: [
      {
        rotate: '126.69deg',
      },
    ],
  },
  ribbon: {
    position: 'absolute',
    width: 219,
    height: 217,
    left: -33,
    bottom: 40,
    transform: [
      {
        rotate: '-4.29deg',
      },
    ],
  },
});
